import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2Icon,
  MapPinnedIcon,
  PlusIcon,
  MinusIcon,
  SearchIcon,
} from 'lucide-react';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@noor/ui';
import { useDebouncedCallback } from '@noor/lib';
import { MAP_CUSTOMIZATION, useYmaps, type YmapsBindings } from '@noor/maps';
import {
  geocoderSearch,
  pointToSearchText,
  TASHKENT_CENTER,
  type LegacyGeoResult,
} from '@/entities/legacy-geo';
import { BranchPin } from '@/entities/branch';

interface LocationPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  address: string;
  latitude: number;
  longitude: number;
  /** Commits the picked point (+ its resolved address text) on "Ok". */
  onConfirm: (result: { address: string; latitude: number; longitude: number }) => void;
}

const PICK_ZOOM = 15;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 6;

/** The bit of the ymaps3 `YMap` instance driven imperatively (see `moveCamera`). */
interface YMapHandle {
  setLocation?: (location: Record<string, unknown>) => void;
  update?: (options: Record<string, unknown>) => void;
  zoom?: number;
}

/**
 * The branch address picker, opened as its OWN dialog over the branch wizard
 * (Figma-referenced screenshot: search box + map + explicit "Ok").
 *
 * Nested inside the wizard's `Modal` — itself a `Dialog` — via Radix's portal,
 * which stacks correctly without any special wiring.
 *
 * The draft is only MOUNTED while `open`, so it seeds from the current
 * address/coordinates via plain `useState` initializers rather than a
 * re-seeding effect (the same construction the promotion option picker and
 * review filter sheet use — see `use-form-seed`'s doc comment for why an
 * effect-based re-seed is the wrong tool here). Closing without confirming
 * (backdrop, Esc, X) leaves the branch's stored address untouched.
 */
export function LocationPickerDialog(props: LocationPickerDialogProps) {
  const { open, onOpenChange } = props;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && <LocationPickerDialogContent {...props} />}
    </Dialog>
  );
}

function LocationPickerDialogContent({
  onOpenChange,
  address,
  latitude,
  longitude,
  onConfirm,
}: LocationPickerDialogProps) {
  const { t } = useTranslation('eats');
  const { bindings, isDisabled } = useYmaps();

  const isPlaced = latitude !== 0 || longitude !== 0;
  const [draftPoint, setDraftPoint] = useState<[number, number]>(
    isPlaced ? [longitude, latitude] : TASHKENT_CENTER,
  );
  const [draftAddress, setDraftAddress] = useState(address);
  const [searchText, setSearchText] = useState(address);
  const [results, setResults] = useState<LegacyGeoResult[] | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const latestRequest = useRef(0);
  const mapRef = useRef<YMapHandle | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const moveCamera = useCallback((next: Record<string, unknown>) => {
    const map = mapRef.current;
    if (!map) return;
    if (typeof map.setLocation === 'function') {
      map.setLocation(next);
      return;
    }
    map.update?.({ location: next });
  }, []);

  const resolveAddress = useCallback(async (longitude: number, latitude: number) => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setIsResolving(true);
    try {
      const [found] = await geocoderSearch(pointToSearchText([longitude, latitude]), 1);
      if (latestRequest.current !== requestId) return;
      if (found?.address) {
        setDraftAddress(found.address);
        setSearchText(found.address);
      }
    } catch {
      // Swallowed: a failed lookup leaves the pin placed and the text as-is.
    } finally {
      if (latestRequest.current === requestId) setIsResolving(false);
    }
  }, []);

  // The camera follows `draftPoint` through the controlled `location` prop
  // (see `MapCanvas`): the same update that moves the pin recenters the
  // view, so placing a point never needs an imperative flight.
  const placePoint = useCallback(
    (longitude: number, latitude: number) => {
      setDraftPoint([longitude, latitude]);
      void resolveAddress(longitude, latitude);
    },
    [resolveAddress],
  );

  const runSearch = useDebouncedCallback((text: string) => {
    if (text.trim() === '') {
      setResults(null);
      return;
    }
    void (async () => {
      try {
        // The Geocoder HTTP API, not the suggest service: `suggestAddresses`
        // (`suggest-maps.yandex.uz/suggest-geo`) never answers a browser
        // `fetch` — no CORS headers, and the POST shape triggers a preflight
        // the endpoint doesn't handle — so every lookup threw and the list
        // stayed empty. This is the same `geocoderSearch` backend the legacy
        // drawer's `LegacyAddressSearch` searches through, Tashkent-biased
        // via `ll`+`spn`+`rspn=1` inside.
        const found = await geocoderSearch(text, SEARCH_LIMIT);
        setResults(found);
      } catch {
        setResults([]);
      }
    })();
  }, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setResults(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
    };
  }, []);

  const pickSuggestion = (result: LegacyGeoResult) => {
    setResults(null);
    setSearchText(result.address);
    setDraftAddress(result.address);
    // Camera is NOT flown imperatively here: `draftPoint` already drives both
    // the pin and the controlled `location` below from the same value, and a
    // second `setLocation` driver raced that prop and displaced the camera
    // away from the freshly placed pin.
    setDraftPoint([result.point.long, result.point.lat]);
  };

  const handleMapClick = useCallback(
    (_object: unknown, event: { coordinates?: [number, number] }) => {
      const [long, lat] = event.coordinates ?? [];
      if (typeof lat === 'number' && typeof long === 'number') placePoint(long, lat);
    },
    [placePoint],
  );

  const zoomBy = useCallback(
    (delta: number) => {
      const current = mapRef.current?.zoom;
      const from = typeof current === 'number' ? current : PICK_ZOOM;
      moveCamera({ zoom: from + delta, duration: 200 });
    },
    [moveCamera],
  );

  const locate = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        placePoint(position.coords.longitude, position.coords.latitude);
      },
      () => {
        // Denied/unavailable: the picker stays exactly as it was.
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };

  const handleRef = useCallback((instance: YMapHandle | null) => {
    if (instance) mapRef.current = instance;
  }, []);

  const handleConfirm = () => {
    const [long, lat] = draftPoint;
    onConfirm({ address: draftAddress, latitude: lat, longitude: long });
    onOpenChange(false);
  };

  return (
    <DialogContent
      className="flex max-h-[85vh] w-full max-w-3xl flex-col gap-4 rounded-4xl sm:max-w-3xl"
      focusOnOpen={false}
    >
      <DialogHeader className="flex-row items-center justify-between gap-4 space-y-0">
        <DialogTitle>{t('branches.form.addressDialogTitle')}</DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div ref={rootRef} className="relative flex-1">
          <label className="flex h-11 items-center gap-2 rounded-full border border-input bg-card px-4 focus-within:border-input-border-focus">
            <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
            <input
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                runSearch(event.target.value);
              }}
              placeholder={t('branches.form.addressSearchPlaceholder')}
              className="h-full w-full min-w-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </label>

          {results !== null && (
            <ul className="absolute top-full right-0 left-0 z-10 mt-1 max-h-[240px] overflow-y-auto rounded-2xl border border-surface-border bg-surface-bg p-1 shadow-lg">
              {results.length === 0 ? (
                <li className="p-3 text-sm text-muted-foreground">
                  {t('branches.form.addressSearchEmpty')}
                </li>
              ) : (
                results.map((result, index) => (
                  <li key={`${result.address}-${index}`}>
                    <button
                      type="button"
                      onClick={() => {
                        pickSuggestion(result);
                      }}
                      className="flex w-full cursor-pointer flex-col items-start gap-0.5 rounded-xl p-3 text-left transition-colors hover:bg-muted"
                    >
                      <span className="truncate text-sm font-medium text-foreground">
                        {result.address}
                      </span>
                      {result.subtitle !== undefined && result.subtitle !== '' && (
                        <span className="truncate text-xs text-muted-foreground">
                          {result.subtitle}
                        </span>
                      )}
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>

        <Button type="button" onClick={handleConfirm} className="shrink-0">
          {t('branches.form.addressConfirm')}
        </Button>
      </div>

      <div className="relative isolate h-[420px] overflow-hidden rounded-2xl">
        {isDisabled || !bindings ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 bg-muted/30 text-muted-foreground">
            <MapPinnedIcon className="size-6 opacity-50" />
            <p className="text-center text-sm">{t('venue.map.unavailable')}</p>
          </div>
        ) : (
          <MapCanvas
            bindings={bindings}
            center={draftPoint}
            isPlaced={isPlaced || draftPoint[0] !== TASHKENT_CENTER[0]}
            onClick={handleMapClick}
            onRef={handleRef}
          />
        )}

        <div className="absolute top-3 right-3 z-10 flex flex-col overflow-hidden rounded-xl shadow-md">
          <button
            type="button"
            onClick={() => {
              zoomBy(1);
            }}
            className="flex size-9 items-center justify-center bg-surface-bg text-foreground transition-colors hover:bg-muted"
          >
            <PlusIcon className="size-4" />
          </button>
          <div className="h-px bg-surface-border" />
          <button
            type="button"
            onClick={() => {
              zoomBy(-1);
            }}
            className="flex size-9 items-center justify-center bg-surface-bg text-foreground transition-colors hover:bg-muted"
          >
            <MinusIcon className="size-4" />
          </button>
        </div>

        <button
          type="button"
          onClick={locate}
          aria-label={t('branches.form.locateMe')}
          className="absolute right-3 bottom-3 z-10 flex size-9 items-center justify-center rounded-full bg-surface-bg text-foreground shadow-md transition-colors hover:bg-muted"
        >
          <LocateIcon className="size-4" />
        </button>
      </div>

      <p className="min-h-[1.25rem] px-1 text-xs text-muted-foreground">
        {isResolving
          ? t('branches.form.pickResolving')
          : draftAddress || t('branches.form.pickHint')}
        {isResolving && (
          <Loader2Icon className="ml-1 inline size-3 animate-spin align-[-1px]" />
        )}
      </p>
    </DialogContent>
  );
}

/** Navigation-arrow "locate me" glyph, matching the legacy order-create control. */
function LocateIcon({ className }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M5.20976 9.39607L1.50729 8.16191C-0.502428 7.49201 -0.502429 4.64932 1.50729 3.97942L13.0947 0.116933C14.818 -0.457495 16.4575 1.18198 15.8831 2.90526L12.0206 14.4927C11.3507 16.5024 8.50799 16.5024 7.83809 14.4927L6.60393 10.7902C6.38452 10.132 5.868 9.61548 5.20976 9.39607Z"
        fill="currentColor"
      />
    </svg>
  );
}

interface MapCanvasProps {
  bindings: YmapsBindings;
  center: [number, number];
  isPlaced: boolean;
  onClick: (object: unknown, event: { coordinates?: [number, number] }) => void;
  onRef: (instance: YMapHandle | null) => void;
}

/**
 * Isolated so the reactified components' dynamic typing (`Record<string, any>`,
 * per `YmapsBindings`) stays contained to this one component, the same split
 * `legacy-order-map.tsx`'s `MapCanvas` uses.
 */
function MapCanvas({ bindings, center, isPlaced, onClick, onRef }: MapCanvasProps) {
  // Reactified ymaps3 components are dynamically typed at the CDN boundary —
  // narrowed to `YMap`'s null check below, same as `legacy-order-map.tsx`.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { ymaps3, reactify } = bindings;
  const components = useMemo(() => reactify.module(ymaps3), [reactify, ymaps3]);
  const {
    YMap,
    YMapDefaultSchemeLayer,
    YMapDefaultFeaturesLayer,
    YMapMarker,
    YMapListener,
  } = components;
  /**
   * The SOLE camera driver, memoized on the point (the `legacy-branch-map`
   * pattern): the pin and the view share this one value, so they cannot
   * disagree. A fresh object every render would re-apply the camera on each
   * search keystroke and fight the merchant's own panning.
   */
  const location = useMemo(() => ({ center, zoom: PICK_ZOOM }), [center]);
  if (!YMap) return null;

  return (
    <YMap
      ref={onRef}
      location={location}
      mode="vector"
      style={{ width: '100%', height: '100%' }}
    >
      <YMapDefaultSchemeLayer customization={MAP_CUSTOMIZATION} />
      <YMapDefaultFeaturesLayer />
      {YMapListener && <YMapListener onClick={onClick} />}

      {isPlaced && (
        <YMapMarker coordinates={center}>
          <BranchPin />
        </YMapMarker>
      )}
    </YMap>
  );
}
